import { Component, inject, computed, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ApplicationsService, AuthService, SettingsBreadcrumbService } from "@shared/index";
import { IDashboardModel } from "@app/views-interfaces/dashboard-model.interface";
import { ApplicationType } from "@app/views-interfaces/ApplicationType.enum";

interface SettingsTile {
    id: string;
    titleKey: string;
    descriptionKey: string;
    route: string;
    requiredPermission: string;
    visible: boolean;
    isVisible: (accessibleDashboards: IDashboardModel[]) => boolean;
}

@Component({
    selector: "wk-settings-main",
    templateUrl: "./settings-main.component.html",
    styleUrl: "./settings-main.component.scss",
    standalone: false,
    host: { class: "wk-sm" },
})
export class SettingsMainComponent {
    private router = inject(Router);
    private authService = inject(AuthService);
    private breadcrumbService = inject(SettingsBreadcrumbService);
    private applicationsService = inject(ApplicationsService);

    allTiles = signal<SettingsTile[]>([
        {
            id: "general",
            titleKey: "Settings.Menu.Title.General",
            descriptionKey: "Settings.Menu.Description.General",
            route: "/settings/general",
            requiredPermission: "can_manage_general_settings",
            visible: false,
            isVisible: (accessibleDashboards: IDashboardModel[]) =>
                this.hasPermission("general", "can_manage_general_settings", accessibleDashboards),
        },
        {
            id: "mappingofstatuses",
            titleKey: "Settings.Menu.Title.MappingOfStatuses",
            descriptionKey: "Settings.Menu.Description.MappingOfStatuses",
            route: "/settings/mappingofstatuses",
            requiredPermission: "can_manage_general_settings",
            visible: false,
            isVisible: (accessibleDashboards: IDashboardModel[]) =>
                this.hasPermission("mapping", "can_manage_general_settings", accessibleDashboards),
        },
        {
            id: "categories",
            titleKey: "Settings.Menu.Title.Categories",
            descriptionKey: "Settings.Menu.Description.Categories",
            route: "/settings/categories",
            requiredPermission: "can_manage_general_settings",
            visible: false,
            isVisible: (accessibleDashboards: IDashboardModel[]) =>
                this.hasPermission("categories", "can_manage_general_settings", accessibleDashboards),
        },
        {
            id: "audittrail",
            titleKey: "Settings.Menu.Title.AuditTrail",
            descriptionKey: "Settings.Menu.Description.AuditTrail",
            route: "/settings/audittrail",
            requiredPermission: "can_manage_general_settings",
            visible: false,
            isVisible: (accessibleDashboards: IDashboardModel[]) =>
                this.hasPermission("audit-trail", "can_manage_general_settings", accessibleDashboards),
        },
        {
            id: "dashboardmanagement",
            titleKey: "Settings.Menu.Title.DashboardManagement",
            descriptionKey: "Settings.Menu.Description.DashboardManagement",
            route: "/settings/dashboardmanagement",
            requiredPermission: "can_manage_dashboards_management_settings",
            visible: false,
            isVisible: (accessibleDashboards: IDashboardModel[]) =>
                this.hasPermission("dashboardmanagement", "can_manage_dashboards_management_settings", accessibleDashboards),
        },
    ]);
    accessibleDashboards = signal<IDashboardModel[]>([]);

    readonly tiles = computed<SettingsTile[]>(() => {
        const tiles = this.allTiles();
        const accessibleDashboards = this.accessibleDashboards();
        if (!tiles || !tiles.length) return [];

        return tiles.filter((tile) => {
            tile.visible = this.hasPermission(tile.id, tile.requiredPermission, accessibleDashboards);
            return tile.visible;
        });
    });

    constructor() {
        this.breadcrumbService.setBreadcrumbs("main");
        Promise.resolve().then(async () => {
            const accessibleDashboards = await this.applicationsService.getAccessibleDashboards();
            this.accessibleDashboards.set(accessibleDashboards);
        });
    }

    hasPermission(tileId: string, tileRequiredPermission: string, accessibleDashboards: IDashboardModel[]): boolean {
        if (!this.authService.currentUser) {
            return false;
        }

        if (tileId === "categories" && !accessibleDashboards.some((d) => d.ApplicationType !== ApplicationType.JobsBilling)) {
            return false;
        }

        // Check if user is admin (matching ViewBag.IsAdmin logic from dashboard)
        const isAdmin = this.authService.currentUser?.ifirm_userid === "-10";
        if (isAdmin) {
            return true;
        }
        switch (tileRequiredPermission) {
            case "can_manage_general_settings":
                return this.authService.currentUser.can_manage_general_settings === "True";
            case "can_manage_dashboards_management_settings":
                return this.authService.currentUser.can_manage_dashboards_management_settings === "True";
            default:
                return false;
        }
    }

    onTileClick(tile: SettingsTile) {
        if (tile.visible) {
            this.router.navigate([tile.route]);
        }
    }
}
